import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
    {children}
  </div>
);
const Toggle = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-3 py-1.5 text-sm border transition-colors ${on ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-400 hover:bg-zinc-100"}`}>{children}</button>
);

export const MeasurementForm = ({ pvp, cfg, onSaved }) => {
  const interventions = pvp.interventions || [];
  const lastIv = interventions.length ? interventions[interventions.length - 1].nummer : 1;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const isNew = f.soort === "meting" && f.iv > interventions.length;
  const n = Number(f.waarde);
  const range = f.waarde === undefined || f.waarde === "" ? "" : (cfg.lo !== null && n < cfg.lo) || (cfg.hi !== null && n > cfg.hi) ? `Kies een waarde tussen ${cfg.lo ?? "…"}${cfg.sfx} en ${cfg.hi ?? "…"}${cfg.sfx}.` : "";
  const ready = f.datum && f.waarde !== "" && f.waarde !== undefined && f.soort && !range && (f.soort === "nulmeting" || (f.iv && (!isNew || (f.omschrijving || "").trim())));

  const openForm = () => {
    const hasMeas = (pvp.measurements || []).some((m) => m.soort === "meting");
    setF({ datum: today(), waarde: "", soort: hasMeas ? "meting" : "nulmeting", iv: interventions.length ? lastIv : 1, omschrijving: "", opmerking: "" });
    setOpen(true);
  };
  const save = async () => {
    try {
      const { data } = await api.post(`/pvps/${pvp.id}/measurements`, {
        datum: f.datum, waarde: Number(f.waarde), soort: f.soort, opmerking: f.opmerking,
        interventie_nummer: f.soort === "meting" ? f.iv : null, interventie_omschrijving: isNew ? f.omschrijving : "",
      });
      onSaved(data);
      setOpen(false);
      toast.success("Meting toegevoegd");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
    }
  };

  return (
    <>
      <Button onClick={openForm} className="rounded-none bg-zinc-900 hover:bg-zinc-700" data-testid="measurement-add-button">
        <Plus className="h-4 w-4 mr-1" /> Voeg meetgegevens toe
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="measurement-dialog">
          <DialogHeader>
            <DialogTitle className="font-grotesk">Meting #{(pvp.measurements || []).length + 1}</DialogTitle>
            <DialogDescription>{cfg.what || "Voer de gemeten waarde in volgens het meetplan."}{cfg.freq && ` Er wordt ${cfg.freq} gemeten.`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Soort meting">
              <div className="flex gap-2">
                <Toggle on={f.soort === "nulmeting"} onClick={() => set("soort", "nulmeting")} testid="measurement-type-nulmeting">Nulmeting</Toggle>
                <Toggle on={f.soort === "meting"} onClick={() => set("soort", "meting")} testid="measurement-type-meting">Meting na interventie</Toggle>
              </div>
            </Field>
            {f.soort === "meting" && (
              <Field label="Interventie">
                <div className="flex flex-wrap gap-2">
                  {interventions.map((i) => (
                    <Toggle key={i.nummer} on={f.iv === i.nummer} onClick={() => set("iv", i.nummer)} testid={`measurement-iv-${i.nummer}`}>{i.nummer}. {i.omschrijving}</Toggle>
                  ))}
                  <Toggle on={isNew} onClick={() => set("iv", interventions.length + 1)} testid="measurement-iv-new">+ Interventie {interventions.length + 1}</Toggle>
                </div>
                {isNew && <Input placeholder="Korte omschrijving, bijv. wekelijkse feedbackgrafiek" value={f.omschrijving} onChange={(e) => set("omschrijving", e.target.value)} className="rounded-none border-zinc-400 mt-2" data-testid="measurement-iv-description" />}
              </Field>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Field label={`Datum${cfg.freq ? ` (${cfg.freq})` : ""}`}>
                <Input type="date" value={f.datum || ""} onChange={(e) => set("datum", e.target.value)} className="rounded-none border-zinc-400" data-testid="measurement-date-input" />
              </Field>
              <Field label={`${cfg.label}${cfg.sfx ? ` (${cfg.sfx})` : ""}${cfg.lo !== null || cfg.hi !== null ? ` · ${cfg.lo ?? "…"}–${cfg.hi ?? "…"}` : ""}`}>
                <Input type="number" step={cfg.step} min={cfg.lo ?? undefined} max={cfg.hi ?? undefined} value={f.waarde ?? ""} onChange={(e) => set("waarde", e.target.value)} className="rounded-none border-zinc-400" data-testid="measurement-value-input" />
              </Field>
            </div>
            {range && <p className="text-sm text-red-600" data-testid="measurement-range-error">{range}</p>}
            <Field label="Opmerking (optioneel)">
              <Input value={f.opmerking || ""} onChange={(e) => set("opmerking", e.target.value)} className="rounded-none border-zinc-400" data-testid="measurement-note-input" />
            </Field>
            <Button onClick={save} disabled={!ready} className="w-full rounded-none bg-zinc-900 hover:bg-zinc-700" data-testid="measurement-save-button">Meting opslaan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
